import { formatAmount, type Message, type MessageLevel, type TaskState, type TaskView } from "idfweb-tasks";

/**
 * The page's toasts: one stack over the bottom left corner of `corner` for
 * everything the page, the workbench and the runtime worker have to say, drawn
 * as VS Code's notification toasts.
 *
 * A top-level task is a one-line toast for as long as it runs, with a progress
 * bar along its bottom edge; its children show only in its numbers. A message and
 * a finished task stay for `LIFETIME_MS` of their level and then go; the
 * timers stop while the pointer is on the stack. A question stays until one
 * of its buttons or the close button is clicked.
 */
export interface Toasts {
    /** Show `text` with a button for each action; the first is the default. Closing answers undefined. */
    ask(level: MessageLevel, text: string, actions: readonly string[]): Promise<string | undefined>;
    dispose(): void;
}

/** How long a toast stays once nothing more will change in it. */
export const LIFETIME_MS: Record<MessageLevel, number> = { info: 5000, warn: 12000, error: 15000 };

interface Entry {
    readonly root: HTMLElement;
    readonly icon: HTMLElement;
    readonly body: HTMLElement;
    /** When it goes, or undefined while it stays up. */
    deadline: number | undefined;
}

interface Row {
    root: HTMLElement;
    aside: HTMLElement;
    barOuter: HTMLElement;
    bar: HTMLElement;
    error: HTMLElement;
}

export function createToasts(parent: HTMLElement, view: TaskView, corner: HTMLElement): Toasts {
    const stack = document.createElement("div");
    stack.className = "toasts";
    stack.setAttribute("role", "log");
    stack.setAttribute("aria-live", "polite");
    parent.appendChild(stack);

    // Over the corner of the workbench: the output panel is to its right on
    // a wide screen and under it on a narrow one, and its newest lines stay clear.
    const place = (): void => {
        const box = corner.getBoundingClientRect();
        stack.style.left = `${box.left + MARGIN}px`;
        stack.style.bottom = `${window.innerHeight - box.bottom + MARGIN}px`;
        stack.style.maxWidth = `${Math.max(0, box.width - 2 * MARGIN)}px`;
        stack.style.maxHeight = `${Math.max(0, box.height - 2 * MARGIN)}px`;
    };
    const resized = new ResizeObserver(place);
    resized.observe(corner);
    window.addEventListener("resize", place);
    place();

    const entries = new Map<string, Entry>();
    /** Toasts that went and are not to come back while their source still lists them. */
    const gone = new Set<string>();
    const taskRows = new Map<string, Row>();
    let messagesSeen = 0;
    let questionsAsked = 0;
    /** What each open question's close button answers. */
    const unanswered = new Map<string, () => void>();
    let pausedAt: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const add = (key: string): Entry => {
        const root = document.createElement("div");
        const icon = document.createElement("span");
        const body = document.createElement("div");
        body.className = "toast-body";
        const close = document.createElement("button");
        close.type = "button";
        close.className = "toast-close codicon codicon-close";
        close.title = "Clear Notification";
        close.setAttribute("aria-label", close.title);
        close.addEventListener("click", () => remove(key));
        root.append(icon, body, close);
        const entry: Entry = { root, icon, body, deadline: undefined };
        entries.set(key, entry);
        stack.appendChild(root);
        return entry;
    };

    const remove = (key: string): void => {
        const entry = entries.get(key);
        if (entry === undefined) return;
        entries.delete(key);
        taskRows.delete(key);
        unanswered.get(key)?.();
        unanswered.delete(key);
        gone.add(key);
        entry.root.remove();
        schedule();
    };

    const expireIn = (entry: Entry, level: MessageLevel): void => {
        entry.deadline ??= Date.now() + LIFETIME_MS[level];
    };

    // One timer, for the entry that goes first. While the pointer is on the
    // stack nothing goes, and the deadlines move on by the time it was there.
    const schedule = (): void => {
        if (timer !== undefined) clearTimeout(timer);
        timer = undefined;
        if (pausedAt !== undefined) return;
        const now = Date.now();
        let next = Infinity;
        for (const [key, entry] of entries) {
            if (entry.deadline === undefined) continue;
            if (entry.deadline <= now) remove(key);
            else next = Math.min(next, entry.deadline);
        }
        if (next !== Infinity) timer = setTimeout(schedule, next - now);
    };
    stack.addEventListener("pointerenter", () => {
        pausedAt = Date.now();
        schedule();
    });
    stack.addEventListener("pointerleave", () => {
        const paused = Date.now() - (pausedAt ?? Date.now());
        pausedAt = undefined;
        for (const entry of entries.values()) {
            if (entry.deadline !== undefined) entry.deadline += paused;
        }
        schedule();
    });

    const showTask = (task: TaskState): void => {
        const key = `task:${task.id}`;
        if (gone.has(key)) return;
        const entry = entries.get(key) ?? add(key);
        let row = taskRows.get(key);
        if (row === undefined) {
            row = newRow(task);
            taskRows.set(key, row);
            entry.body.appendChild(row.root);
            entry.root.appendChild(row.barOuter);
        }
        updateRow(row, task);
        const level = task.state === "failed" ? "error" : "info";
        setLevel(entry, level, task.state === "running");
        if (task.state !== "running") expireIn(entry, level);
    };

    const showMessage = (message: Message, index: number): void => {
        const entry = add(`message:${index}`);
        setLevel(entry, message.level, false);
        const line = document.createElement("div");
        line.className = "toast-text";
        line.textContent = message.text;
        entry.body.appendChild(line);
        expireIn(entry, message.level);
    };

    const render = (): void => {
        for (const root of view.roots) showTask(root);
        const messages = view.messages;
        for (; messagesSeen < messages.length; messagesSeen++) {
            showMessage(messages[messagesSeen]!, messagesSeen);
        }
        schedule();
    };

    const ask = (level: MessageLevel, text: string, actions: readonly string[]) =>
        new Promise<string | undefined>((resolve) => {
            const key = `question:${questionsAsked++}`;
            const entry = add(key);
            setLevel(entry, level, false);
            const line = document.createElement("div");
            line.className = "toast-text";
            line.textContent = text;
            const buttons = document.createElement("div");
            buttons.className = "toast-actions";
            actions.forEach((action, i) => {
                const button = document.createElement("button");
                button.type = "button";
                button.className = i === 0 ? "toast-action primary" : "toast-action";
                button.textContent = action;
                button.addEventListener("click", () => {
                    unanswered.set(key, () => resolve(action));
                    remove(key);
                });
                buttons.appendChild(button);
            });
            entry.body.append(line, buttons);
            unanswered.set(key, () => resolve(undefined));
        });

    const stop = view.onChange(render);
    render();

    return {
        ask,
        dispose(): void {
            stop();
            resized.disconnect();
            window.removeEventListener("resize", place);
            if (timer !== undefined) clearTimeout(timer);
            stack.remove();
        },
    };
}

const ICONS: Record<MessageLevel, string> = { info: "info", warn: "warning", error: "error" };

function setLevel(entry: Entry, level: MessageLevel, running: boolean): void {
    entry.root.className = `toast ${level}`;
    entry.icon.className = running
        ? "toast-icon codicon codicon-loading codicon-modifier-spin"
        : `toast-icon codicon codicon-${ICONS[level]}`;
}

/** How far the stack keeps from the edges of its corner, in pixels. */
const MARGIN = 12;

function newRow(task: TaskState): Row {
    const root = document.createElement("div");
    root.className = "task";
    root.innerHTML =
        '<div class="label"><span class="what"></span><span class="aside"></span></div>' + '<div class="error"></div>';
    const find = (selector: string) => root.querySelector(selector) as HTMLElement;
    find(".what").textContent = task.label;
    const barOuter = document.createElement("div");
    barOuter.className = "bar-outer";
    const bar = document.createElement("div");
    bar.className = "bar";
    barOuter.appendChild(bar);
    return { root, aside: find(".aside"), barOuter, bar, error: find(".error") };
}

// What the task is on now goes in the tooltip: the output panel has it too.
function updateRow(row: Row, task: TaskState): void {
    const running = task.state === "running";
    row.root.title = running ? (task.detail ?? "") : "";
    row.aside.textContent = task.state === "done" ? took(task) : formatAmount(task.done, task.total, task.unit);
    row.error.textContent = task.state === "failed" ? (task.error ?? "failed") : "";
    row.barOuter.hidden = !running;
    // A task whose size is not known yet gets a bar that sweeps instead of one
    // that stays at nothing.
    row.barOuter.classList.toggle("unknown", task.total === undefined);
    row.bar.style.width = task.total === undefined ? "" : `${percent(task.done, task.total)}%`;
}

function took(task: TaskState): string {
    const seconds = ((task.endedAt ?? Date.now()) - task.startedAt) / 1000;
    return seconds < 1 ? "done" : `done in ${seconds.toFixed(1)} s`;
}

function percent(part: number, whole: number): number {
    return whole > 0 ? (100 * part) / whole : 0;
}
